import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import CTAButton from '../components/CTAButton';
import TrustSignals from '../components/TrustSignals';
import * as FiIcons from 'react-icons/fi';

const { FiMapPin, FiCheck, FiShield } = FiIcons;

const LocalizedSEOPage = ({ city, product }) => {
  // Format city name for display
  const cityName = city.split('-').map(word => 
    word.charAt(0).toUpperCase() + word.slice(1)
  ).join(' ');

  // Product configuration
  const productConfig = {
    'general-liability': {
      title: 'General Liability',
      description: 'Covers customer injuries, property damage, and lawsuits',
      benefits: [
        'Protects against slip-and-fall accidents',
        'Covers property damage claims',
        'Includes legal defense costs',
        'Starting at $19/month'
      ],
      example: `A customer slips in your ${cityName} business and gets injured. General liability covers their medical bills and protects you from lawsuits.`
    },
    'bop': {
      title: 'Business Owner Policy (BOP)',
      description: 'Combines general liability and property coverage in one policy',
      benefits: [
        'General liability protection',
        'Commercial property coverage', 
        'Business income protection',
        'Starting at $25/month'
      ],
      example: `A fire damages your ${cityName} retail store. BOP covers property repairs, lost inventory, and income during closure.`
    },
    'workers-compensation': {
      title: 'Workers Compensation',
      description: 'Covers workplace injuries and is required by California law',
      benefits: [
        'Covers medical expenses for injured employees',
        'Provides lost wage replacement',
        'Required by California law',
        'Starting at $14/month'
      ],
      example: `An employee at your ${cityName} business gets injured on the job. Workers comp covers their medical treatment and lost wages.`
    }
  };

  const config = productConfig[product];
  const ctaText = `Get Your ${cityName} ${config.title} Quote Now`;

  // City-specific image mapping
  const cityImages = {
    'los-angeles': 'https://images.unsplash.com/photo-1544427920-c49ccfb85579?ixlib=rb-4.0.3&auto=format&fit=crop&w=2062&q=80',
    'san-diego': 'https://images.unsplash.com/photo-1524338198850-8a2ff63aaceb?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80',
    'san-jose': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
    'san-francisco': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
    'fresno': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
    'sacramento': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
    'oakland': 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80',
    'long-beach': 'https://images.unsplash.com/photo-1524338198850-8a2ff63aaceb?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80',
    'anaheim': 'https://images.unsplash.com/photo-1524338198850-8a2ff63aaceb?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80',
    'santa-ana': 'https://images.unsplash.com/photo-1524338198850-8a2ff63aaceb?ixlib=rb-4.0.3&auto=format&fit=crop&w=2069&q=80'
  };

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-primary-600 to-primary-800 text-white py-20">
        <div className="absolute inset-0 bg-black opacity-60"></div>
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-40"
          style={{
            backgroundImage: `url(${cityImages[city] || cityImages['los-angeles']})`
          }}
        ></div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="flex items-center justify-center mb-4"
            >
              <SafeIcon icon={FiMapPin} className="h-6 w-6 mr-2" />
              <span className="text-lg">{cityName}, California</span>
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-4xl md:text-6xl font-bold mb-6"
            >
              {config.title} Insurance for {cityName} Small Businesses
            </motion.h1>
            
            <motion.p 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-xl md:text-2xl mb-8 max-w-3xl mx-auto"
            >
              Protect your {cityName} business with Next Insurance {config.title}. Get your quote in 10 minutes!
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <CTAButton text={ctaText} size="xl" className="mb-8" />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <TrustSignals className="text-white" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Why {cityName} Businesses Choose {config.title}
              </h2>
              <ul className="space-y-4">
                {config.benefits.map((benefit, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    className="flex items-start space-x-3"
                  >
                    <SafeIcon icon={FiCheck} className="h-6 w-6 text-secondary-600 mt-0.5 flex-shrink-0" />
                    <span className="text-lg text-gray-700">{benefit}</span>
                  </motion.li>
                ))}
              </ul>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="mt-8"
              >
                <CTAButton text={ctaText} />
              </motion.div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-primary-50 p-8 rounded-lg"
            >
              <h3 className="text-2xl font-bold text-gray-900 mb-6">Local {cityName} Coverage</h3>
              <div className="space-y-4 text-lg text-gray-700">
                <p>• Tailored for California businesses</p>
                <p>• Complies with local regulations</p>
                <p>• Instant online quotes</p>
                <p>• No agent appointments needed</p>
                <p>• Same-day coverage available</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Local Relevance Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              Serving {cityName} Small Businesses
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Burbank Insurance Services helps {cityName} small businesses like restaurants and retail shops secure affordable {config.title} coverage tailored to local risks.
            </p>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="bg-primary-50 p-8 rounded-lg max-w-4xl mx-auto"
          >
            <div className="flex items-start space-x-4">
              <SafeIcon icon={FiShield} className="h-8 w-8 text-primary-600 mt-1 flex-shrink-0" />
              <div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">Local Example</h3>
                <p className="text-lg text-gray-700">{config.example}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-primary-700 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.h2 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-3xl md:text-4xl font-bold mb-6"
          >
            Get Your {cityName} {config.title} Coverage Today
          </motion.h2>
          
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-xl mb-8"
          >
            Protect your {cityName} business with Next Insurance. Get your instant quote now.
          </motion.p>
          
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <CTAButton 
              text="Get Your Instant Quote Now" 
              size="xl" 
              className="bg-white text-primary-600 hover:bg-gray-100" 
            />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default LocalizedSEOPage;